import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DxFormComponent } from 'devextreme-angular';
import { ToastrService } from 'ngx-toastr';
import { 
  EventPublishingSettingsCreationAndUpdateModel 
} from 'src/app/models/event-publishing-settings/event-publishing-settings-creation-and-update.model';
import { EventPublishingSettingsModel } from 'src/app/models/event-publishing-settings/event-publishing-settings.model';
import { LoadingService } from 'src/app/services/loading.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { EventPublishingSettingsService } from 'src/app/services/event-publishing-settings.service';
import { ConfirmationDialogData } from '../shared/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-tenant-event-publishing-settings-form',
  standalone: false,
  templateUrl: './tenant-event-publishing-settings-form.component.html',
  styleUrl: './tenant-event-publishing-settings-form.component.scss',
})
export class TenantEventPublishingSettingsFormComponent {
  @Input({ required: true }) 
  eventPublishingSettings!: EventPublishingSettingsModel | null;

  @Output()
  eventPublishingSettingsChange = 
    new EventEmitter<EventPublishingSettingsModel | null>(); 

  @Input() 
  isTenantPublishedToSubscribers = false;

  @Input() 
  readOnly = false;

  @ViewChild('formBasicData', { static: false })
  formBasicData!: DxFormComponent;

  @ViewChild('formPublishingModeData', { static: false })
  formPublishingModeData!: DxFormComponent;

  formData: EventPublishingSettingsCreationAndUpdateModel = {
    name: '',
    description: '',
    publishInCatalog: false,
    publishToSubscribers: false
  }

  showDeleteSettingsModal: boolean = false;
  modalDeleteSettingsConfirmationOptions: ConfirmationDialogData | null = null;
  
  constructor(
    private translateService: TranslateService,
    private loadingService: LoadingService,
    private clipboard: Clipboard,
    private toastrService: ToastrService,
    private eventPublishingSettingsService: EventPublishingSettingsService
  ) {}

  ngOnInit(): void {
    if(this.eventPublishingSettings) {
      this.patchForm(this.eventPublishingSettings);
    }
  }

  submitForm(): void {
    const isBasicFormValid = this.formBasicData.instance.validate().isValid;
    const isPublishingModeFormValid = this.formPublishingModeData.instance.validate().isValid;

    if(!isBasicFormValid || !isPublishingModeFormValid) {
      this.toastrService.error(
        this.translateService.instant('VALIDATIONS.VALUES-NOT-VALID')
      );
      return;
    }

    if(!this.isTenantPublishedToSubscribers) {
      this.formData.publishToSubscribers = false;
    }

    this.loadingService.load(
      this.eventPublishingSettingsService.upsertEventPublishingSettings(
        this.formData
      )
    ).subscribe({
        next: (result) => {
          this.eventPublishingSettings = result;
          this.eventPublishingSettingsChange.emit(result);

          this.toastrService.success(
            this.translateService.instant(
              this.eventPublishingSettings 
                ? 'EVENT-PUBLISHING-SETTINGS.EVENT-PUBLISHING-SETTINGS-UPDATED'
                : 'EVENT-PUBLISHING-SETTINGS.EVENT-PUBLISHING-SETTINGS-CREATED'
            )
          );
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
          );
        }
      });
  }

  private patchForm(settings: EventPublishingSettingsModel) {
    this.formData.name = settings.name;
    this.formData.description = settings.description;
    this.formData.publishInCatalog = settings.publishInCatalog;
    this.formData.publishToSubscribers = settings.publishToSubscribers;
  }

  private resetFormData() {
    this.formData = {
      name: '',
      description: '',
      publishInCatalog: false,
      publishToSubscribers: false
    };
  }

  copyEventOverviewLink() {
    if(!this.eventPublishingSettings) return;

    const url =
      window.location.origin + '/' +
      this.eventPublishingSettings.accessToken +
      '/events';

    if (this.clipboard.copy(url)) {
      this.toastrService.success(
        this.translateService.instant("COPY-LINK.COPY-SUCCESS")
      );
    } else {
      this.toastrService.warning(
        this.translateService.instant("COPY-LINK.COPY-FAILURE")
      );
    }
  }

  openDeletePublishingSettingsConfirmationModal() {
    this.modalDeleteSettingsConfirmationOptions = {
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.DELETE'),
      message: this.translateService.instant(
        'EVENT-PUBLISHING-SETTINGS.DELETE-EVENT-PUBLISHING-SETTINGS-CONFIRMATION-DIALOG.TEXT'
      ),
      dismissButtonText: this.translateService.instant('COMMON.CANCEL'),
    };
    this.showDeleteSettingsModal = true;
  }

  deleteEventPublishingSettings() {
    this.loadingService.load(
      this.eventPublishingSettingsService.deleteEventPublishingSettings()
    ).subscribe(() => {
      this.showDeleteSettingsModal = false;
      this.toastrService.success(
        this.translateService.instant(
          'EVENT-PUBLISHING-SETTINGS.EVENT-PUBLISHING-SETTINGS-DELETED'
        )
      );

      this.eventPublishingSettings = null;
      this.eventPublishingSettingsChange.emit(null);
      this.resetFormData();
    })
  }
}
